import { format, isWithinInterval, parseISO } from "date-fns";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex, Icon, Icons, OutageAlert, Text, UnstyledButton } from "src/components";
import { Color, FontSize, FontWeight } from "src/theme";
import type { NormalisedSystemOutagesDto, OutageDto, SortDirection } from "src/types";
import { BE_DATE_FORMAT, DATE_FORMAT, localeFormat, SHORT_WEEKDAY_NAME_FORMAT } from "src/utils";
import { useToday } from "../utils";

export const DateHeader: FC<{ date: Date; isHighlighted: boolean }> = ({ date, isHighlighted }) => {
    const fontWeight = isHighlighted ? FontWeight._700 : FontWeight._400;

    return (
        <Flex flexDirection="row" alignItems="center" justifyContent="center">
            <Text color={Color.white} size={FontSize._16} weight={fontWeight}>
                {localeFormat(date, SHORT_WEEKDAY_NAME_FORMAT).slice(0, 2)} - {localeFormat(date, DATE_FORMAT)}
            </Text>
        </Flex>
    );
};

export const SystemHeader: FC<{ onSortDirectionChange: () => void; sortDirection: SortDirection }> = ({
    onSortDirectionChange,
    sortDirection,
}) => {
    const { t } = useTranslation();

    return (
        <StyledHeaderButton onClick={onSortDirectionChange}>
            <Flex flexDirection="row" alignItems="center" justifyContent="space-between" gap="0.7rem">
                <Text color={Color.white} size={FontSize._16} weight={FontWeight._400}>
                    {t("outage.systemName")}
                </Text>
                <Flex flexDirection="column" gap="5px">
                    <Icon icon={Icons.ArrowUpFilled} height={6} width={9} color={sortDirection === "desc" ? Color.white : Color.white30} />
                    <Icon icon={Icons.ArrowDownFilled} height={6} width={9} color={sortDirection === "asc" ? Color.white : Color.white30} />
                </Flex>
            </Flex>
        </StyledHeaderButton>
    );
};

export const SystemCell: FC<{ system: NormalisedSystemOutagesDto }> = ({ system }) => {
    const { name, outages } = system;
    const today = useToday();
    const todayDate = useMemo(() => format(today, BE_DATE_FORMAT), [today]);

    const isHighlighted = useMemo(() => {
        return outages[todayDate]?.some((outage) => {
            const now = new Date();
            const start = parseISO(outage.start);
            const end = outage.end ? parseISO(outage.end) : parseISO(outage.expectedEnd);

            return isWithinInterval(now, { start, end });
        });
    }, [outages, todayDate]);

    return (
        <StyledCell>
            <Text weight={isHighlighted ? FontWeight._700 : FontWeight._400}>{name}</Text>
        </StyledCell>
    );
};

// @ts-ignore todo: types
export const OutageCell: FC<{ outages: OutageDto[]; isSameDay: boolean }> = ({ outages, isSameDay }) => {
    const now = useMemo(() => new Date(), []);

    const isActive = useCallback(
        (outage: OutageDto) => {
            const start = parseISO(outage.start);
            const end = outage.end ? parseISO(outage.end) : parseISO(outage.expectedEnd);

            return isWithinInterval(now, { start, end }) && isSameDay;
        },
        [isSameDay, now],
    );

    if (!outages) {
        return null;
    }

    return outages.map((outage) => (
        <StyledCell active={isActive(outage)} justifyContent="center" flexWrap="wrap" gap="0.5rem">
            <OutageAlert key={outage.id} outage={outage} />
        </StyledCell>
    ));
};

const StyledHeaderButton = styled(UnstyledButton)`
    padding: 0.75rem 1rem;
    width: 100%;
    &:hover {
        background-color: ${({ theme }) => theme.color.blue150};
    }
`;

const StyledCell = styled(Flex)<{ active?: boolean }>`
    padding: 1rem;
    background-color: ${({ active }) => (active ? Color.red10 : "transparent")};
`;
